import { isDefined } from "@omni/utils/helper.utils";
import { partitionAndFind } from "@omni/utils/object.utils";
import { uniq } from "lodash-es";
import { ThunkDispatch } from "redux-thunk";

import { NumericFacetBounds } from "@/components/Search/SearchResults/components/results/components/productsResults/components/facets/components/facet/@types/numericFacet.types";
import {
    searchWithNewComponentConfiguration,
    searchWithNewConfiguration
} from "@/redux/modules/value/search/helpers/search.helpers";
import { set } from "@/redux/modules/value/search/search.module";
import {
    SearchConfiguration,
    SearchInvalidations,
    SearchStatus
} from "@/redux/modules/value/search/search.types";
import { IState, TExtraThunkArgs, TValueAction } from "@/redux/store.types";
import * as AlgoliaUserService from "@/services/algolia/algoliaUser.service";
import {
    getHierarchicalFacetName,
    toHierarchicalFacetName
} from "@/services/bffSearch/responseTransformers/mergeHierarchicalFacets.transformer";
import { SearchComponentOfType } from "@/utils/search/types/searchComponent";
import {
    FacetFilter,
    FacetOperator,
    FacetsConfiguration,
    NumericFilter,
    SearchComponentConfiguration
} from "@/utils/search/types/searchConfiguration";

const configureSearch = (
    configuration: Partial<SearchConfiguration>,
    invalidations: SearchInvalidations
) =>
    searchWithNewConfiguration(
        configuration,
        invalidations,
        (_configuration, searchState) => ({
            ...searchState.configuration,
            ...configuration
        })
    );

const configureSearchComponent = (
    configuration: SearchComponentConfiguration,
    invalidations: SearchInvalidations
) =>
    searchWithNewComponentConfiguration(
        configuration.component,
        invalidations,
        () => configuration
    );

const selectFacetValue = (
    component: SearchComponentOfType<"FACETS">,
    filter: Pick<FacetFilter, "name" | "operator">,
    selectedValue: string,
    invalidations: SearchInvalidations,
    sendEvent: (facetName: string, facetValue: string) => void
) => {
    sendEvent(filter.name, selectedValue);
    return searchWithNewComponentConfiguration(
        component,
        invalidations,
        (componentConfiguration) => {
            const [currentFacetFilter, otherFacetFilters] = partitionAndFind(
                componentConfiguration?.facetFilters ?? [],
                (facetFilter) => facetFilter.name === filter.name
            );

            const updatedFacetFilter: FacetFilter = {
                ...filter,
                selectedValues: uniq([
                    ...(currentFacetFilter?.selectedValues ?? []),
                    selectedValue
                ])
            };

            return {
                ...componentConfiguration,
                facetFilters: [...otherFacetFilters, updatedFacetFilter]
            };
        }
    );
};

const selectHierarchicalFacetValue = (
    component: SearchComponentOfType<"FACETS">,
    facetName: string,
    selectedValue: string,
    level: number,
    invalidations: SearchInvalidations,
    sendEvent: (facetName: string, facetValue: string) => void
) => {
    sendEvent(facetName, selectedValue);
    return searchWithNewComponentConfiguration(
        component,
        invalidations,
        (componentConfiguration) => {
            const nonHierarchicalFacetFilters =
                componentConfiguration.facetFilters.filter(
                    (facetFilter) =>
                        getHierarchicalFacetName(facetFilter.name) !== facetName
                );

            const hierarchicalFacetFilter: FacetFilter = {
                name: toHierarchicalFacetName(facetName, level),
                operator: FacetOperator.OR,
                selectedValues: [selectedValue]
            };

            return {
                ...componentConfiguration,
                facetFilters: [
                    ...nonHierarchicalFacetFilters,
                    hierarchicalFacetFilter
                ]
            };
        }
    );
};

const clearHierarchicalFacet = (
    component: SearchComponentOfType<"FACETS">,
    facetName: string,
    invalidations: SearchInvalidations
) =>
    searchWithNewComponentConfiguration(
        component,
        invalidations,
        (componentConfiguration) => ({
            ...componentConfiguration,
            facetFilters: componentConfiguration.facetFilters.filter(
                (facetFilter) =>
                    getHierarchicalFacetName(facetFilter.name) !== facetName
            )
        })
    );

const setNumericFacet = (
    component: SearchComponentOfType<"FACETS">,
    facetName: string,
    boundaries: NumericFacetBounds | undefined,
    invalidations: SearchInvalidations
) => {
    return searchWithNewComponentConfiguration(
        component,
        invalidations,
        (
            componentConfiguration,
            _otherComponentConfigurations,
            _searchState
        ): FacetsConfiguration => {
            const [numericFilter, otherNumericFilters] = partitionAndFind(
                componentConfiguration.numericFilters,
                (numericFilter) => numericFilter.name === facetName
            );

            if (!boundaries) {
                return {
                    ...componentConfiguration,
                    numericFilters: [...otherNumericFilters]
                };
            }

            const newNumericFilter: NumericFilter = {
                name: facetName,
                lowerBound: isDefined(boundaries.lowerBound)
                    ? boundaries.lowerBound
                    : numericFilter?.lowerBound,
                upperBound: isDefined(boundaries.upperBound)
                    ? boundaries.upperBound
                    : numericFilter?.upperBound
            };

            return {
                ...componentConfiguration,
                numericFilters: [...otherNumericFilters, newNumericFilter]
            };
        }
    );
};

const clearFacetValues = (
    component: SearchComponentOfType<"FACETS">,
    facetName: string,
    valuesToClear: Array<string>,
    invalidations: SearchInvalidations
) =>
    searchWithNewComponentConfiguration(
        component,
        invalidations,
        (
            componentConfiguration,
            _otherComponentConfigurations,
            _searchState
        ): FacetsConfiguration => {
            const keepFilterValue = (value: string) =>
                !valuesToClear.includes(value);
            const toClearedFacetFilter = (facetFilter: FacetFilter) => ({
                ...facetFilter,
                ...(facetFilter.name === facetName && {
                    selectedValues:
                        facetFilter.selectedValues.filter(keepFilterValue)
                })
            });

            return {
                ...componentConfiguration,
                facetFilters: componentConfiguration.facetFilters
                    .map(toClearedFacetFilter)
                    .filter((facetFilter) => facetFilter.selectedValues.length)
            };
        }
    );

const generateAlgoliaParameters =
    () =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TValueAction>) => {
        const algoliaParameters =
            await AlgoliaUserService.generateSearchParameters(dispatch);

        return algoliaParameters;
    };

const setSearchStatus =
    (searchStatus: SearchStatus) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TValueAction>) => {
        dispatch(
            set({
                status: searchStatus
            })
        );
    };

export {
    clearFacetValues,
    clearHierarchicalFacet,
    configureSearch,
    configureSearchComponent,
    generateAlgoliaParameters,
    selectFacetValue,
    selectHierarchicalFacetValue,
    setNumericFacet,
    setSearchStatus
};
